export const USER_SETTING_KEYS = {
  // The storage key is kept for backward compatibility with saved user settings.
  editorOpenContextMenu: "hotkeys.editor.insert_entity",
  editorOpenItem: "hotkeys.editor.open_item",
  editorOpenLocation: "hotkeys.editor.open_location",
  editorOpenUnit: "hotkeys.editor.open_unit",
  editorOpenSkill: "hotkeys.editor.open_skill",
  editorOpenNews: "hotkeys.editor.open_news",
  editorOpenQuest: "hotkeys.editor.open_quest",
  editorBatchInsert: "hotkeys.editor.batch_insert",
  searchOpen: "hotkeys.search.open",
} as const;

export type UserSettingKey =
  (typeof USER_SETTING_KEYS)[keyof typeof USER_SETTING_KEYS];

export type HotkeyDefinition = {
  key: UserSettingKey;
  label: string;
  description: string;
  defaultValue: string;
};

export type EditorHotkeyModalType =
  | "item"
  | "location"
  | "unit"
  | "skill"
  | "news"
  | "quest"
  | "batch-insert";

export const EDITOR_HOTKEY_MODAL_TYPES: Readonly<
  Partial<Record<UserSettingKey, EditorHotkeyModalType>>
> = {
  [USER_SETTING_KEYS.editorOpenItem]: "item",
  [USER_SETTING_KEYS.editorOpenLocation]: "location",
  [USER_SETTING_KEYS.editorOpenUnit]: "unit",
  [USER_SETTING_KEYS.editorOpenSkill]: "skill",
  [USER_SETTING_KEYS.editorOpenNews]: "news",
  [USER_SETTING_KEYS.editorOpenQuest]: "quest",
  [USER_SETTING_KEYS.editorBatchInsert]: "batch-insert",
};

export const HOTKEY_DEFINITIONS: readonly HotkeyDefinition[] = [
  {
    key: USER_SETTING_KEYS.editorOpenContextMenu,
    label: "Открыть контекстное меню в редакторе",
    description: "Открывает контекстное меню у курсора в редакторе статьи.",
    defaultValue: "Mod+Alt+E",
  },
  {
    key: USER_SETTING_KEYS.editorOpenItem,
    label: "Открыть выбор предмета в редакторе",
    description: "Открывает окно выбора предмета в текстовом редакторе.",
    defaultValue: "",
  },
  {
    key: USER_SETTING_KEYS.editorOpenLocation,
    label: "Открыть выбор локации в редакторе",
    description: "Открывает окно выбора локации в текстовом редакторе.",
    defaultValue: "",
  },
  {
    key: USER_SETTING_KEYS.editorOpenUnit,
    label: "Открыть выбор юнита в редакторе",
    description: "Открывает окно выбора юнита в текстовом редакторе.",
    defaultValue: "",
  },
  {
    key: USER_SETTING_KEYS.editorOpenSkill,
    label: "Открыть выбор умения в редакторе",
    description: "Открывает окно выбора умения в текстовом редакторе.",
    defaultValue: "",
  },
  {
    key: USER_SETTING_KEYS.editorOpenNews,
    label: "Открыть выбор новости в редакторе",
    description: "Открывает окно выбора новости в текстовом редакторе.",
    defaultValue: "",
  },
  {
    key: USER_SETTING_KEYS.editorOpenQuest,
    label: "Открыть выбор квеста в редакторе",
    description: "Открывает окно выбора квеста в текстовом редакторе.",
    defaultValue: "",
  },
  {
    key: USER_SETTING_KEYS.editorBatchInsert,
    label: "Открыть пакетный ввод в редакторе",
    description: "Открывает пакетный ввод сущностей в текстовом редакторе.",
    defaultValue: "",
  },
  {
    key: USER_SETTING_KEYS.searchOpen,
    label: "Открыть поиск и установить курсор",
    description:
      "Открывает глобальный поиск административной панели и переносит курсор в поисковую строку.",
    defaultValue: "Mod+Alt+S",
  },
] as const;

export const HOTKEY_DEFAULTS: Record<UserSettingKey, string> =
  HOTKEY_DEFINITIONS.reduce(
    (defaults, definition) => ({
      ...defaults,
      [definition.key]: definition.defaultValue,
    }),
    {} as Record<UserSettingKey, string>
  );

const modifierOrder = ["Mod", "Ctrl", "Meta", "Alt", "Shift"] as const;
const modifierAliases: Record<string, (typeof modifierOrder)[number]> = {
  mod: "Mod",
  ctrl: "Ctrl",
  control: "Ctrl",
  meta: "Meta",
  cmd: "Meta",
  command: "Meta",
  alt: "Alt",
  option: "Alt",
  shift: "Shift",
};

const reservedShortcuts = new Set([
  "Mod+L",
  "Mod+N",
  "Mod+O",
  "Mod+P",
  "Mod+R",
  "Mod+T",
  "Mod+W",
  "Mod+Shift+N",
  "Mod+Shift+P",
  "Mod+Shift+T",
  "Mod+Shift+W",
]);

const keyAliases: Record<string, string> = {
  esc: "Escape",
  return: "Enter",
  space: "Space",
  spacebar: "Space",
  del: "Delete",
  left: "ArrowLeft",
  right: "ArrowRight",
  up: "ArrowUp",
  down: "ArrowDown",
};

const supportedNamedKeys = new Set([
  "Enter",
  "Escape",
  "Space",
  "Tab",
  "Backspace",
  "Delete",
  "Home",
  "End",
  "PageUp",
  "PageDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
]);

export type HotkeyValidation =
  | { valid: true; value: string }
  | { valid: false; error: string };

export type HotkeyKeyboardEvent = Pick<
  KeyboardEvent,
  "altKey" | "code" | "ctrlKey" | "isComposing" | "key" | "metaKey" | "shiftKey"
>;

export function isUserSettingKey(value: string): value is UserSettingKey {
  return HOTKEY_DEFINITIONS.some((definition) => definition.key === value);
}

export function normalizeHotkey(value: string): HotkeyValidation {
  const trimmedValue = value.trim();
  if (trimmedValue === "") {
    return { valid: true, value: "" };
  }

  const parts = trimmedValue.split("+").map((part) => part.trim());
  if (parts.length < 2 || parts.some((part) => part === "")) {
    return invalid(
      "Сочетание должно содержать модификатор и одну основную клавишу."
    );
  }

  const modifiers = new Set<(typeof modifierOrder)[number]>();
  let key: string | null = null;

  for (const part of parts) {
    const modifier = modifierAliases[part.toLowerCase()];
    if (modifier) {
      if (modifiers.has(modifier)) {
        return invalid(
          "В сочетании не должно быть повторяющихся модификаторов."
        );
      }
      modifiers.add(modifier);
      continue;
    }

    if (key !== null) {
      return invalid(
        "Сочетание должно содержать только одну основную клавишу."
      );
    }

    const normalizedKey = normalizeKey(part);
    if (!normalizedKey) {
      return invalid(
        "Эта клавиша не поддерживается. Используйте букву, цифру, F1–F12 или служебную клавишу."
      );
    }
    key = normalizedKey;
  }

  if (key === null || modifiers.size === 0) {
    return invalid(
      "Добавьте Ctrl, Cmd или другой модификатор и одну основную клавишу."
    );
  }

  if (
    !modifiers.has("Mod") &&
    !modifiers.has("Ctrl") &&
    !modifiers.has("Meta")
  ) {
    return invalid("Сочетание должно содержать Ctrl, Cmd или Mod.");
  }

  if (
    modifiers.has("Mod") &&
    (modifiers.has("Ctrl") || modifiers.has("Meta"))
  ) {
    return invalid("Mod нельзя использовать вместе с Ctrl или Cmd.");
  }

  const normalized = [
    ...modifierOrder.filter((modifier) => modifiers.has(modifier)),
    key,
  ].join("+");

  if (reservedShortcuts.has(normalized)) {
    return invalid(
      "Это сочетание зарезервировано распространёнными браузерами."
    );
  }

  return { valid: true, value: normalized };
}

export function hotkeyFromKeyboardEvent(
  event: HotkeyKeyboardEvent
): HotkeyValidation {
  const key = keyFromKeyboardEvent(event);
  if (!key) {
    return invalid("Добавьте основную клавишу к сочетанию.");
  }

  const modifiers: string[] = [];
  if (event.ctrlKey && event.metaKey) {
    modifiers.push("Ctrl", "Meta");
  } else if (event.ctrlKey || event.metaKey) {
    modifiers.push("Mod");
  }
  if (event.altKey) modifiers.push("Alt");
  if (event.shiftKey) modifiers.push("Shift");

  return normalizeHotkey([...modifiers, key].join("+"));
}

/**
 * Checks a keyboard event against a configured shortcut without changing the
 * event. Callers decide whether handling it is appropriate for their context
 * and only then call preventDefault.
 */
export function matchesHotkeyEvent(
  event: HotkeyKeyboardEvent,
  value: string
): boolean {
  if (event.isComposing) return false;

  const normalized = normalizeHotkey(value);
  const eventKey = keyFromKeyboardEvent(event);
  if (!normalized.valid || !eventKey) return false;

  const parts = normalized.value.split("+");
  const key = parts.pop();
  if (key !== eventKey) return false;

  const modifiers = new Set(parts);
  const expectsMod = modifiers.has("Mod");
  const expectsCtrl = modifiers.has("Ctrl");
  const expectsMeta = modifiers.has("Meta");
  const hasPortableMod = event.ctrlKey !== event.metaKey;

  if (expectsMod ? !hasPortableMod : event.ctrlKey !== expectsCtrl) {
    return false;
  }
  if (!expectsMod && event.metaKey !== expectsMeta) return false;

  return (
    event.altKey === modifiers.has("Alt") &&
    event.shiftKey === modifiers.has("Shift")
  );
}

/**
 * Global shortcuts must not take over native text-entry controls or another
 * contenteditable editor. The editor's own handler is scoped to its view.
 */
export function isEditableHotkeyTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;

  return Boolean(
    target.closest(
      "input, textarea, select, [contenteditable]:not([contenteditable='false']), [role='textbox']"
    )
  );
}

export function findHotkeyConflict(
  values: Record<UserSettingKey, string>,
  key: UserSettingKey
): UserSettingKey | null {
  const current = normalizeHotkey(values[key]);
  if (!current.valid || current.value === "") return null;

  for (const definition of HOTKEY_DEFINITIONS) {
    if (definition.key === key) continue;
    const candidate = normalizeHotkey(values[definition.key]);
    if (
      candidate.valid &&
      candidate.value !== "" &&
      candidate.value === current.value
    ) {
      return definition.key;
    }
  }

  return null;
}

export function formatHotkey(
  value: string,
  isApple = isApplePlatform()
): string {
  if (value.trim() === "") return "Не назначено";

  const normalized = normalizeHotkey(value);
  if (!normalized.valid) return value;

  const labels: Record<string, string> = isApple
    ? {
        Mod: "⌘",
        Ctrl: "⌃",
        Meta: "⌘",
        Alt: "⌥",
        Shift: "⇧",
      }
    : {
        Mod: "Ctrl",
        Ctrl: "Ctrl",
        Meta: "Cmd",
        Alt: "Alt",
        Shift: "Shift",
      };

  return normalized.value
    .split("+")
    .map((part) => labels[part] ?? formatKey(part))
    .join(" + ");
}

export function describeHotkey(value: string): string {
  if (value.trim() === "") return "не назначено";

  const normalized = normalizeHotkey(value);
  if (!normalized.valid) return value;

  const labels: Record<string, string> = {
    Mod: "Control или Command",
    Ctrl: "Control",
    Meta: "Command",
    Alt: "Alt",
    Shift: "Shift",
  };

  return normalized.value
    .split("+")
    .map((part) => labels[part] ?? formatKey(part))
    .join(" плюс ");
}

function invalid(error: string): HotkeyValidation {
  return { valid: false, error };
}

function normalizeKey(value: string): string | null {
  const alias = keyAliases[value.toLowerCase()];
  if (alias) return alias;
  if (/^[a-z]$/i.test(value)) return value.toUpperCase();
  if (/^[0-9]$/.test(value)) return value;
  if (/^f(?:[1-9]|1[0-2])$/i.test(value)) return value.toUpperCase();
  return supportedNamedKeys.has(value) ? value : null;
}

function keyFromKeyboardEvent(event: HotkeyKeyboardEvent): string | null {
  const modifierKeys = new Set(["Control", "Meta", "Alt", "Shift"]);
  if (modifierKeys.has(event.key)) return null;

  const codeMatch = /^(?:Key([A-Z])|Digit([0-9]))$/.exec(event.code);
  if (codeMatch) return codeMatch[1] ?? codeMatch[2];

  return normalizeKey(event.key);
}

function formatKey(value: string): string {
  const labels: Record<string, string> = {
    Space: "Пробел",
    Enter: "Enter",
    Escape: "Esc",
    Backspace: "Backspace",
    Delete: "Delete",
    ArrowLeft: "←",
    ArrowRight: "→",
    ArrowUp: "↑",
    ArrowDown: "↓",
    PageUp: "Page Up",
    PageDown: "Page Down",
  };
  return labels[value] ?? value;
}

function isApplePlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/i.test(
    navigator.platform || navigator.userAgent
  );
}
